import { useEffect, useState } from "react";
import "@fontsource/playfair-display/400.css";
import "@fontsource/playfair-display/500.css";
import "@fontsource/playfair-display/600.css";
import "@fontsource/playfair-display/700.css";
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";

// Components
import Navigation from "@/components/Navigation";
import HeroSection from "@/components/HeroSection";
import AboutSection from "@/components/AboutSection";
import MissionsSection from "@/components/MissionsSection";
import EcosystemSection from "@/components/EcosystemSection";
import NetworkSection from "@/components/NetworkSection";
import FiguresSection from "@/components/FiguresSection";
import ContactSection from "@/components/ContactSection";
import Footer from "@/components/Footer";
import LegalPage from "@/components/LegalPage";
import PrivacyPage from "@/components/PrivacyPage";
import AdminDashboard from "@/components/admin/AdminDashboard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Main One Page Component
const OnePage = () => {
  const [content, setContent] = useState({});
  const [members, setMembers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [images, setImages] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Seed data first
        await axios.post(`${API}/seed`).catch(() => {});
        
        // Fetch all content
        const [contentRes, membersRes, settingsRes, imagesRes] = await Promise.all([
          axios.get(`${API}/content`),
          axios.get(`${API}/members`),
          axios.get(`${API}/settings`),
          axios.get(`${API}/images/list/public`).catch(() => ({ data: [] }))
        ]);
        
        // Convert content array to object keyed by section_key
        const contentObj = {};
        contentRes.data.forEach(section => {
          contentObj[section.section_key] = section;
        });
        
        // Convert images array to object keyed by section
        const imagesObj = {};
        imagesRes.data.forEach(img => {
          if (!imagesObj[img.section]) {
            imagesObj[img.section] = [];
          }
          imagesObj[img.section].push(img);
        });
        
        setContent(contentObj);
        setMembers(membersRes.data);
        setSettings(settingsRes.data);
        setImages(imagesObj);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 font-inter">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      <Navigation settings={settings} />
      
      <main>
        {content.hero?.is_visible !== false && (
          <HeroSection content={content.hero} images={images.hero} />
        )}
        
        {content.about?.is_visible !== false && (
          <AboutSection content={content.about} images={images.about} />
        )}
        
        {content.network?.is_visible !== false && (
          <NetworkSection content={content.network} images={images.network} />
        )}
        
        {content.missions?.is_visible !== false && (
          <MissionsSection content={content.missions} images={images.missions} />
        )}
        
        {content.ecosystem?.is_visible !== false && (
          <EcosystemSection content={content.ecosystem} members={members} images={images.ecosystem} />
        )}
        
        {content.figures?.is_visible !== false && (
          <FiguresSection content={content.figures} />
        )}
        
        {content.contact?.is_visible !== false && (
          <ContactSection content={content.contact} settings={settings} />
        )}
      </main>
      
      <Footer settings={settings} content={content.footer} />
      <Toaster position="bottom-right" />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<OnePage />} />
          <Route path="/mentions-legales" element={<LegalPage />} />
          <Route path="/confidentialite" element={<PrivacyPage />} />
          <Route path="/admin/*" element={<AdminDashboard />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
